import { SimUI } from '../sim_ui.js';
import { Sim } from '../sim.js';
import { EventID, TypedEvent } from '../typed_event.js';
import { wowheadSupportedLanguages } from '../constants/lang.js';
import { BooleanPicker } from '../components/boolean_picker.js';
import { EnumPicker } from '../components/enum_picker.js';
import { NumberPicker } from '../components/number_picker.js';
import { BaseModal } from './base_modal.js';
import { Tooltip } from 'bootstrap';

export class SettingsMenu extends BaseModal {
	private readonly simUI: SimUI;

	constructor(parent: HTMLElement, simUI: SimUI) {
		super(parent, 'settings-menu', { title: "配置", footer: true });
		this.simUI = simUI;

		this.body.innerHTML = `
			<div class="picker-group">
				<div class="fixed-rng-seed-container">
					<div class="fixed-rng-seed"></div>
					<div class="form-text">
						<span>最后一次使用的随机种子：</span>&nbsp;<span class="last-used-rng-seed">0</span>
					</div>
				</div>
				<div class="language-picker within-raid-sim-hide"></div>
			</div>
			<div class="show-threat-metrics-picker w-50 pe-2"></div>
			<div class="show-experimental-picker w-50 pe-2"></div>
		`;
		this.footer!.innerHTML = `
			<button
				class="restore-defaults-button btn btn-primary"
			>恢复默认</button>
		`

		const restoreDefaultsButton = this.rootElem.getElementsByClassName('restore-defaults-button')[0] as HTMLElement;
		Tooltip.getOrCreateInstance(restoreDefaultsButton, {
			title: "恢复所有默认设置（装备、消耗、BUFF、天赋、EP 权重等），保存的设置将被保留。"
		});
		restoreDefaultsButton.addEventListener('click', event => {
			this.simUI.applyDefaults(TypedEvent.nextEventID());
		});

		const fixedRngSeed = this.rootElem.getElementsByClassName('fixed-rng-seed')[0] as HTMLElement;
		new NumberPicker(fixedRngSeed, this.simUI.sim, {
			label: '固定随机种子',
			labelTooltip: '进行伤害模拟时使用的随机数生成器的随机种子，0表示每次运行使用不同的随机种子。设置此值可共享精确的模拟结果或进行调试。',
			extraCssClasses: ['mb-0'],
			changedEvent: (sim: Sim) => sim.fixedRngSeedChangeEmitter,
			getValue: (sim: Sim) => sim.getFixedRngSeed(),
			setValue: (eventID: EventID, sim: Sim, newValue: number) => {
				sim.setFixedRngSeed(eventID, newValue);
			},
		});

		const lastUsedRngSeed = this.rootElem.getElementsByClassName('last-used-rng-seed')[0] as HTMLElement;
		lastUsedRngSeed.textContent = String(this.simUI.sim.getLastUsedRngSeed());
		this.simUI.sim.lastUsedRngSeedChangeEmitter.on(() => lastUsedRngSeed.textContent = String(this.simUI.sim.getLastUsedRngSeed()));

		const language = this.rootElem.getElementsByClassName('language-picker')[0] as HTMLElement;
		const langs = Object.keys(wowheadSupportedLanguages);
		const defaultLang = langs.indexOf('cn');
		const languagePicker = new EnumPicker(language, this.simUI.sim, {
			label: '语言',
			labelTooltip: '设置技能等鼠标悬浮框的显示语言',
			values: langs.map((lang, i) => {
				return {
					name: wowheadSupportedLanguages[lang],
					value: i,
				};
			}),
			changedEvent: (sim: Sim) => sim.languageChangeEmitter,
			getValue: (sim: Sim) => {
				const idx = langs.indexOf(sim.getLanguage());
				return idx == -1 ? defaultLang : idx;
			},
			setValue: (eventID: EventID, sim: Sim, newValue: number) => {
				sim.setLanguage(eventID, langs[newValue] || 'cn');
			},
		});
		// Refresh page after language change, to apply the changes.
		languagePicker.changeEmitter.on(() => setTimeout(() => location.reload(), 100));

		const showThreatMetrics = this.rootElem.getElementsByClassName('show-threat-metrics-picker')[0] as HTMLElement;
		new BooleanPicker(showThreatMetrics, this.simUI.sim, {
			label: '显示仇恨/坦克选项',
			labelTooltip: '显示与坦克相关的所有选项和指标，如 TPS/DTPS。',
			inline: true,
			changedEvent: (sim: Sim) => sim.showThreatMetricsChangeEmitter,
			getValue: (sim: Sim) => sim.getShowThreatMetrics(),
			setValue: (eventID: EventID, sim: Sim, newValue: boolean) => {
				sim.setShowThreatMetrics(eventID, newValue);
			},
		});

		const showExperimental = this.rootElem.getElementsByClassName('show-experimental-picker')[0] as HTMLElement;
		new BooleanPicker(showExperimental, this.simUI.sim, {
			label: '显示实现性选项',
			labelTooltip: '如果有正在进行的实验性功能，则显示相关选项。',
			inline: true,
			changedEvent: (sim: Sim) => sim.showExperimentalChangeEmitter,
			getValue: (sim: Sim) => sim.getShowExperimental(),
			setValue: (eventID: EventID, sim: Sim, newValue: boolean) => {
				sim.setShowExperimental(eventID, newValue);
			},
		});
	}
}
